import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowDown, ArrowUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { AdminAnalyticsPreferencesBlob } from "@/types/admin-analytics";
import {
  PLATFORM_KPI_CARD_TITLE_KEYS,
  PlatformKpiCardId,
  resolveCardOrder,
  resolveVisibleCards,
} from "../constants";

interface PreferencesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (blob: AdminAnalyticsPreferencesBlob) => void;
  isSaving: boolean;
  preferences?: AdminAnalyticsPreferencesBlob;
}

export const PreferencesDialog: React.FC<PreferencesDialogProps> = ({
  open,
  onOpenChange,
  onSave,
  isSaving,
  preferences,
}) => {
  const { t } = useTranslation();
  const [order, setOrder] = useState<PlatformKpiCardId[]>([]);
  const [visible, setVisible] = useState<Set<PlatformKpiCardId>>(new Set());

  // Re-seed local state from the saved blob each time the dialog opens.
  useEffect(() => {
    if (open) {
      setOrder(resolveCardOrder(preferences));
      setVisible(resolveVisibleCards(preferences));
    }
  }, [open, preferences]);

  const toggleVisible = (id: PlatformKpiCardId, checked: boolean) => {
    setVisible((previous) => {
      const next = new Set(previous);
      if (checked) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });
  };

  const moveCard = (index: number, direction: -1 | 1) => {
    setOrder((previous) => {
      const target = index + direction;
      if (target < 0 || target >= previous.length) {
        return previous;
      }
      const next = [...previous];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const handleSave = () => {
    // PUT is a full replace — keep any unknown keys other clients may have stored.
    onSave({
      ...preferences,
      visibleMetrics: order.filter((id) => visible.has(id)),
      order,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {t("analytics.platform.preferencesDialog.title")}
          </DialogTitle>
          <DialogDescription>
            {t("analytics.platform.preferencesDialog.description")}
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-2">
          {order.map((id, index) => (
            <div
              key={id}
              className="flex items-center justify-between gap-3 rounded-lg border border-base-border px-3 py-2"
            >
              <div className="flex items-center gap-3">
                <Switch
                  id={`kpi-visible-${id}`}
                  checked={visible.has(id)}
                  onCheckedChange={(checked) => toggleVisible(id, checked)}
                />
                <Label htmlFor={`kpi-visible-${id}`} className="text-sm">
                  {t(PLATFORM_KPI_CARD_TITLE_KEYS[id])}
                </Label>
              </div>
              <div className="flex items-center gap-1">
                <Button
                  type="button"
                  variant="new_ghost"
                  size="sm"
                  className="size-7 p-0"
                  onClick={() => moveCard(index, -1)}
                  disabled={index === 0}
                  aria-label={t("analytics.platform.preferencesDialog.moveUp")}
                >
                  <ArrowUp className="size-3.5" />
                </Button>
                <Button
                  type="button"
                  variant="new_ghost"
                  size="sm"
                  className="size-7 p-0"
                  onClick={() => moveCard(index, 1)}
                  disabled={index === order.length - 1}
                  aria-label={t(
                    "analytics.platform.preferencesDialog.moveDown",
                  )}
                >
                  <ArrowDown className="size-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="new_outline"
            onClick={() => onOpenChange(false)}
            disabled={isSaving}
          >
            {t("common.cancel")}
          </Button>
          <Button
            type="button"
            variant="new_default"
            onClick={handleSave}
            disabled={isSaving || visible.size === 0}
          >
            {t("common.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
